/**
 * 全局公司名配置 - 登录后从 get_user_context 获取，存 localStorage
 * 所有页面通过 getCompany() 获取当前公司名，禁止硬编码
 */
const COMPANY_KEY = 'erp_company'
const ABBR_KEY = 'erp_company_abbr'

export function getCompany(): string {
  return localStorage.getItem(COMPANY_KEY) || ''
}

export function setCompany(name: string) {
  if (name) localStorage.setItem(COMPANY_KEY, name)
}

/** 获取公司缩写（用于拼接科目名，如 "麟鑫" / "村斤斤"） */
export function getCompanyAbbr(): string {
  return localStorage.getItem(ABBR_KEY) || ''
}

export function setCompanyAbbr(abbr: string) {
  if (abbr) localStorage.setItem(ABBR_KEY, abbr)
}

export function clearCompany() {
  localStorage.removeItem(COMPANY_KEY)
  localStorage.removeItem(ABBR_KEY)
}

/** 动态拼接科目全名，如 buildAccountName('2241', '其他应付款') → '2241 - 其他应付款 - 麟鑫' */
export function buildAccountName(code: string, accountName: string): string {
  const abbr = getCompanyAbbr()
  return abbr ? `${code} - ${accountName} - ${abbr}` : `${code} - ${accountName}`
}

let _companyAccountCache: Record<string, string> | null = null

/**
 * 读取公司默认科目字段（跨公司通用：211/212 科目代码不同，付款/收款科目禁止前端硬编码）。
 * 返回如 default_payable_account / default_expense_claim_payable_account / default_employee_advance_account 的值。
 *
 * 注意：必须走统一请求实例（@/utils/request），它会注入 X-Frappe-CSRF-Token 并在
 * CSRFTokenError 时自动重取 token 重试。此前用裸 axios 调同一接口必带不上 token →
 * 400 无效请求 → 返回空串 → 调用方回落到硬编码科目（如已禁用的 2202）导致建凭证失败。
 */
export async function getCompanyAccount(field: string): Promise<string> {
  if (_companyAccountCache && _companyAccountCache[field] !== undefined) {
    return _companyAccountCache[field] || ''
  }
  const company = getCompany()
  if (!company) return ''
  try {
    const { default: http } = await import('@/utils/request')
    const res: any = await http.post('/method/custom_app.api.unified.get_doc', {
      doctype: 'Company',
      name: company,
      raw: 1,
    })
    // 响应拦截器已把 message 解包为 { code, success, data }
    const payload: any = res?.data && typeof res.data === 'object' ? res.data : res
    if (payload && typeof payload === 'object' && field in payload) {
      _companyAccountCache = payload
    }
    return (_companyAccountCache || {})[field] || ''
  } catch {
    // 失败不写入缓存：避免一次抖动把整会话的默认科目全部锁死为空串
    return ''
  }
}

/** 从公司名提取简称作为ERP标题，如 "石家庄麟鑫化工有限公司" → "麟鑫ERP" */
export function getAppTitle(): string {
  const c = getCompany()
  if (!c) return 'ERP'
  // 去掉地名前缀和"有限公司"后缀，取核心词
  const m = c.replace(/^(石家庄|四平|北京|上海|广州|深圳|天津|重庆|南京|杭州|武汉|成都|沈阳|长春|哈尔滨|济南|郑州|西安|太原|合肥|长沙|南昌|福州|兰州|银川|西宁|呼和浩特|乌鲁木齐|拉萨|昆明|贵阳|南宁|海口|长春|鞍山|四平|双鸭山|鸡西|鹤岗|伊春|七台河|牡丹江|黑河|绥化|大庆|齐齐哈尔|佳木斯)市?/, '')
    .replace(/(有限公司|股份有限公司|集团|公司)$/, '')
    .replace(/(新材料|化工|科技|贸易|实业|工业|制造)$/, '')
  return (m || c.slice(0, 4)) + 'ERP'
}

/** 返回公司过滤条件，company 为空时返回空对象（不过滤） */
export function companyFilter(): Record<string, string> {
  const c = getCompany()
  return c ? { company: c } : {}
}

let _fetching = false
/** 路由守卫调用：company 为空时自动从后端补全（仅触发一次）
 *  同样必须走统一请求实例：裸 axios 的 POST 带不上 CSRF token，
 *  会话一旦被 Desk 生成过 csrf_token 就会 400，导致 company/abbr 永远补不上。 */
export function ensureCompany() {
  // company 或 abbr 缺失时都补拉（登录只设了 company 会导致 buildAccountName 拼出无后缀科目）
  if ((getCompany() && getCompanyAbbr()) || _fetching) return
  _fetching = true
  import('@/utils/request')
    .then(({ default: http }) => {
      http.post('/method/custom_app.api.unified.get_user_context', {})
        .then((res: any) => {
          const ctx = res || {}
          if (ctx.success && ctx.data?.company) {
            setCompany(ctx.data.company)
            if (ctx.data.abbr) {
              setCompanyAbbr(ctx.data.abbr)
            } else {
              // fallback: 从Company doctype查abbr
              http.post('/method/custom_app.api.unified.get_list', {
                doctype: 'Company', raw: 1, fields: JSON.stringify(['name', 'abbr']), page_size: 1,
              }).then((r2: any) => {
                const list = r2?.data?.list || r2?.list || r2?.data || []
                if (list[0]?.abbr) setCompanyAbbr(list[0].abbr)
              }).catch(() => {})
            }
          }
        })
        .catch(() => {})
        .finally(() => { _fetching = false })
    })
    .catch(() => { _fetching = false })
}
